/**
 * WHERE A PIECE OF WORK CAME FROM, which is how we take it on.
 *
 * A change request arrives as a document, becomes a project of its own and is delivered as
 * one. Everything else lands in one of the three projects every company is made with —
 * Tasks, Bugs and Graphic Design (backend `STARTER_PROJECTS`) — and is worked one item at a
 * time. Knowing which is the first question when deciding what to pick up, so the work page
 * is split by it.
 *
 * A bug is a bug wherever it was raised: one logged against a change-request project is still
 * somebody's broken thing, and belongs in the Bugs lane rather than among the CR's tasks.
 */
export type WorkSource = 'change_request' | 'tasks' | 'bugs' | 'design';

export const WORK_SOURCES: WorkSource[] = ['change_request', 'tasks', 'bugs', 'design'];

export const WORK_SOURCE_LABELS: Record<WorkSource, string> = {
  change_request: 'Change requests',
  tasks: 'Tasks',
  bugs: 'Bugs',
  design: 'Graphic design',
};

export const WORK_SOURCE_HINTS: Record<WorkSource, string> = {
  change_request: 'Work on a change-request project — from the CR document through testing to live',
  tasks: 'Small tasks asked for one at a time',
  bugs: 'Something broken, wherever it was raised',
  design: 'Graphic design work',
};

/** The starter projects' codes. */
const BY_PROJECT_CODE: Record<string, WorkSource> = { TASKS: 'tasks', BUGS: 'bugs', DESIGN: 'design' };

/**
 * BY NAME AS WELL AS BY CODE, because the projects were made by hand before they were made
 * automatically. Production already has "Graphic Design Projects" (GDP) and "Graphic Design &
 * UX/UI Projects" (GDT, GDUUP) — three codes, one kind of work — so anything named for graphic
 * design is design, and a project called just Tasks or Bugs is that, whatever its code.
 */
function byName(name: string): WorkSource | null {
  if (/graphic\s*design/i.test(name)) return 'design';
  if (/^\s*tasks?\s*$/i.test(name)) return 'tasks';
  if (/^\s*bugs?\s*$/i.test(name)) return 'bugs';
  return null;
}

/** A company's own projects — anything not recognised above — are change requests. */
export function sourceOf(item: { type: string; project: { code: string; name: string } }): WorkSource {
  if (item.type === 'bug') return 'bugs';
  return BY_PROJECT_CODE[item.project.code.toUpperCase()] ?? byName(item.project.name) ?? 'change_request';
}
